import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { of, ReplaySubject } from 'rxjs';
import {delay, first, take, takeUntil, tap} from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';

import { FormHelperService } from '../../../../shared/services/form-helper.service';
import { CommissionUpdatePackageService } from '../../../../shared/services/commission-update-package.service';
import { Uuid } from '../../../../shared/interface/response/uuid';
import { InsuranceCompany } from '../../../../shared/interface/insurance-company';
import {
  IC_ALLIANZ,
  IC_COLONNADE,
  IC_COMPENSA,
  IC_ERGO_HESTIA,
  IC_EUROPA, IC_GENERALI, IC_LLOYDS_LIFE, IC_POCZTOWE_TUW,
  IC_PZU,
  IC_RESO,
  IC_SIGNAL_IDUNA, IC_TUW_TUW,
  IC_TUZ_UBEZPIECZENIA,
  IC_UNIQA,
  IC_UNIQA_INTERRISK,
  IC_WARTA,
  IC_WIENER,
  InsuranceCompanyService,
} from '../../../../shared/services/insurance-company.service';
import { DictService } from '../../../../shared/services/dict.service';
import { CountedAndPaginated } from '../../../../shared/interface/response/counted-and-paginated';
import {
  CommissionUpdatePackage,
  CommissionUpdatePackageRisks,
} from '../../../../shared/interface/commission-update-package';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { FilterService } from '../../../../shared/services/filter.service';

@Component({
  selector: 'app-commission-update-package-add',
  templateUrl: './commission-update-package-add.component.html',
  styleUrls: ['./commission-update-package-add.component.scss'],
})
export class CommissionUpdatePackageAddComponent
  implements OnInit, OnDestroy
{
  @Input() commissionUpdatePackageObject!: CommissionUpdatePackage | null;
  @Output() closeModification = new EventEmitter<void>();
  @Output() commissionUpdatePackageCreated = new EventEmitter<string>();
  insuranceCompanyKeys: string[] = [IC_COMPENSA];

  commissionUpdatePackageForm!: FormGroup;
  risksFormArray = new FormArray([]);

  selectedInsuranceCompany!: InsuranceCompany;
  processingRequest = false;
  errors: any = {};

  private destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private router: Router,
    private formHelper: FormHelperService,
    private commissionUpdatePackageService: CommissionUpdatePackageService,
    private insuranceCompanyService: InsuranceCompanyService,
    private dictService: DictService,
    private dialog: MatDialog,
    private filterService: FilterService
  ) {}


  get insuranceCompanyControl(): FormControl {
    return this.commissionUpdatePackageForm.get('insuranceCompany') as FormControl;
  }

  get importerControl(): FormControl {
    return this.commissionUpdatePackageForm.get('importer') as FormControl;
  }

  get companyControl(): FormControl {
    return this.commissionUpdatePackageForm.get('company') as FormControl;
  }

  get partnerControl(): FormControl {
    return this.commissionUpdatePackageForm.get('partner') as FormControl;
  }
  get ofwcaControl(): FormControl {
    return this.commissionUpdatePackageForm.get('ofwca') as FormControl;
  }

  get riskCodeControl(): FormControl {
    return this.commissionUpdatePackageForm.get('riskCode') as FormControl;
  }

  get riskPackControl(): FormControl {
    return this.commissionUpdatePackageForm.get('riskPack') as FormControl;
  }

  get isPackageControl(): FormControl {
    return this.commissionUpdatePackageForm.get('isPackage') as FormControl;
  }
  get activeControl(): FormControl {
    return this.commissionUpdatePackageForm.get('active') as FormControl;
  }

  get isSaveDisabled(): boolean {
    return this.commissionUpdatePackageForm.invalid || this.risksFormArray.length === 0;
  }

  ngOnInit(): void {
    this.initForm();
    this.initInsuranceCompany();
    this.initPartnerOfwcaExclusivity();
    this.addRisk();
    if (this.commissionUpdatePackageObject) {
      this.initialize(this.commissionUpdatePackageObject);
    }

  }



  addRisk(internalRisk?: number, importRate = '', ratePaid = ''): void {
    const group = this.fb.group({
      internalRisk: [internalRisk, Validators.required],
      importRate: [importRate],
      ratePaid: [ratePaid],
    });
    this.risksFormArray.push(group);
  }

  removeRisks(index: number): void {
    this.risksFormArray.removeAt(index);
  }

  initialize(pkg: CommissionUpdatePackage): void {
    this.commissionUpdatePackageForm.patchValue({
      insuranceCompany: pkg.insuranceCompany?.id,
      importer: pkg.importer?.id,
      company: pkg.company?.id,
      partner: pkg.partner?.uuid,
      ofwca: pkg.ofwca?.uuid,
      riskCode: pkg.riskCode,
      riskPack: pkg.riskPack,
      isPackage: pkg.isPackage,
      active: pkg.active,
    });

    this.risksFormArray.clear();
    pkg.risks.forEach((risk) =>
      this.addRisk(risk.risk.id, risk.importRate, risk.ratePaid)
    );
  }


  save(addNext = false): void {
    const value = this.commissionUpdatePackageForm.getRawValue();

    this.commissionUpdatePackageService
      .findCommissionUpdatePackage(value)
      .pipe(take(1))
      .subscribe((result) => {
        const currentId = this.commissionUpdatePackageObject?.id ?? null;
        const duplicates = result.items.filter((i) => i.id !== currentId);
        const obj = this.commissionUpdatePackageObject;
        const isEdit = !!obj;
        const formChanged = isEdit && this.isFormModifiedComparedTo(obj);
        const shouldWarn = (!isEdit && result.items.length > 0) || (isEdit && formChanged && duplicates.length > 0);

        if (shouldWarn) {
          const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '750px',
            data: {
              text: 'Konfiguracja o podanych parametrach już istnieje. Czy chcesz dodać nowy aneks?',
            },
          });
          dialogRef.afterClosed().subscribe((confirmed) => {
            if (confirmed) {
              this.snackBar.open('Pomyślnie dodano aneks', '', { duration: 2000 });
              this.saveForm(addNext);
            }
          });
        } else {
          this.saveForm(addNext);
        }
      });
  }


  saveForm(addNext = false): void {
    this.processingRequest = true;

    const formValue = this.commissionUpdatePackageForm.getRawValue();
    const uuid = this.commissionUpdatePackageObject?.uuid ?? null;

    this.commissionUpdatePackageService
      .sendForm(formValue, uuid)
      .pipe(first())
      .subscribe({
        next: (response: Uuid) => {
          this.snackBar.open('Pomyślnie utworzono konfigurację', '', { duration: 2000 });
          this.processingRequest = false;

          if (addNext) {
            // this.risksFormArray.clear(); // czyści ryzyka
            this.risksFormArray.controls.forEach((group: FormGroup) => {
              group.get('ratePaid')?.reset('');
            });
            this.commissionUpdatePackageForm.get('partner').reset();
            this.commissionUpdatePackageForm.get('ofwca').reset();
            this.commissionUpdatePackageForm.get('riskPack').reset();
            this.commissionUpdatePackageForm.patchValue({
              isPackage: true,
              active: true,
            });  // Ustaw wartości domyślne

            this.initInsuranceCompany(); // Ustaw ponownie TU i importera
            if (this.risksFormArray.length === 0) {
              this.addRisk(); // dodaj puste ryzyko, jeśli ma być widoczne
            }

          } else {
            if (this.commissionUpdatePackageObject) {
              this.closeModification.emit();
            } else {
              this.router.navigate(['/konfigurator/szczegoly/', response.uuid]);
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this.processingRequest = false;
          this.snackBar.open('Błąd: ' + error.message, '', { duration: 3000 });
          if (error.status === 400) {
            this.errors = error.error?.error?.list || {};
            this.formHelper.setBackendErrorState(this.commissionUpdatePackageForm, this.errors);
          }
        },
      });
  }

  cancel(): void {
    if (this.commissionUpdatePackageObject) {
      this.closeModification.emit();
    } else {
      this.router.navigate(['/konfigurator']);
    }
  }

  insuranceCompanyChanged(insuranceCompany: InsuranceCompany): void {
    this.selectedInsuranceCompany = insuranceCompany;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private isFormModifiedComparedTo(obj: CommissionUpdatePackage): boolean {
    const form = this.commissionUpdatePackageForm.getRawValue();
    const risksMatch = this.compareRisks(obj.risks);
    return (
      form.insuranceCompany !== obj.insuranceCompany?.id ||
      form.importer !== obj.importer?.id ||
      form.company !== obj.company?.id ||
      form.partner !== obj.partner ||
      form.ofwca !== obj.ofwca ||
      form.riskCode !== obj.riskCode ||
      form.riskPack !== obj.riskPack ||
      form.isPackage !== obj.isPackage ||
      form.active !== obj.active ||
      !risksMatch
    );
  }

  private compareRisks(risks: CommissionUpdatePackageRisks[]): boolean {
    if (this.risksFormArray.length !== risks.length) {return false;}
    return this.risksFormArray.controls.every((group, index) => {
      const r = risks[index];
      return (
        group.get('internalRisk')?.value === r.risk.id &&
        group.get('importRate')?.value === r.importRate &&
        group.get('ratePaid')?.value === r.ratePaid
      );
    });
  }

  private initForm(): void {
    const isEditing = !!this.commissionUpdatePackageObject;
    this.commissionUpdatePackageForm = this.fb.group({
      insuranceCompany: new FormControl({ value: '', disabled: true }, Validators.required),
      importer: new FormControl({ value: '', disabled: true }, Validators.required),
      company: ['', Validators.required],
      partner: [''],
      ofwca: [''],
      riskCode: ['', Validators.required],
      riskPack: [''],
      risks: this.risksFormArray,
      isPackage: [isEditing ? null : true],
      active: [isEditing ? null : true],
    });
  }
  private initInsuranceCompany(): void {
    this.insuranceCompanyService
      .getAllInsuranceCompanies()
      .pipe(first())
      .subscribe((result: CountedAndPaginated<InsuranceCompany>) => {
        const compensa = result.items.find((i) => i.key === IC_COMPENSA);
        if (compensa) {
          this.selectedInsuranceCompany = compensa;
          this.insuranceCompanyControl.setValue(compensa.id);

          this.dictService
            .getDictSubject()
            .pipe(first())
            .subscribe((dict) => {
              const importer = dict.find(
                (d) => d.type === 'commission_importer_' + compensa.key
              );
              if (importer) {
                this.importerControl.setValue(importer.id);
              }
            });
        }
      });
  }
  private initPartnerOfwcaExclusivity(): void {
    this.partnerControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        if (val) {
          this.ofwcaControl.disable({ emitEvent: false });
          this.ofwcaControl.setValue(null, { emitEvent: false });
        } else {
          this.ofwcaControl.enable({ emitEvent: false });
        }
      });

    this.ofwcaControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        if (val) {
          this.partnerControl.disable({ emitEvent: false });
          this.partnerControl.setValue(null, { emitEvent: false });
        } else {
          this.partnerControl.enable({ emitEvent: false });
        }
      });
  }

}
